/**
 * API client for reference data management
 */
import { ApiClient } from './apiClient';

class ReferenceApiClient extends ApiClient {
  constructor() {
    super();
  }

  // Languages
  async getLanguages(params = {}) {
    return this.get('/reference/languages', { params });
  }

  async getLanguage(id) {
    return this.get(`/reference/languages/${id}`);
  }

  async createLanguage(data) {
    return this.post('/reference/languages', data);
  }

  async updateLanguage(id, data) {
    return this.put(`/reference/languages/${id}`, data);
  }

  async deleteLanguage(id) {
    return this.delete(`/reference/languages/${id}`);
  }

  async searchLanguages(searchData) {
    return this.post('/reference/languages/search', searchData);
  }

  // Platforms
  async getPlatforms(params = {}) {
    return this.get('/reference/platforms', { params });
  }

  async getPlatform(id) {
    return this.get(`/reference/platforms/${id}`);
  }

  async createPlatform(data) {
    return this.post('/reference/platforms', data);
  }

  async updatePlatform(id, data) {
    return this.put(`/reference/platforms/${id}`, data);
  }

  async deletePlatform(id) {
    return this.delete(`/reference/platforms/${id}`);
  }

  // Media Kinds
  async getMediaKinds(params = {}) {
    return this.get('/reference/media-kinds', { params });
  }

  async getMediaKind(id) {
    return this.get(`/reference/media-kinds/${id}`);
  }

  async createMediaKind(data) {
    return this.post('/reference/media-kinds', data);
  }

  async updateMediaKind(id, data) {
    return this.put(`/reference/media-kinds/${id}`, data);
  }

  async deleteMediaKind(id) {
    return this.delete(`/reference/media-kinds/${id}`);
  }

  // Topics
  async getTopics(params = {}) {
    return this.get('/reference/topics', { params });
  }

  async getTopicsTree() {
    return this.get('/reference/topics/tree');
  }

  async getTopic(id) {
    return this.get(`/reference/topics/${id}`);
  }

  async createTopic(data) {
    return this.post('/reference/topics', data);
  }

  async updateTopic(id, data) {
    return this.put(`/reference/topics/${id}`, data);
  }

  async deleteTopic(id) {
    return this.delete(`/reference/topics/${id}`);
  }

  async moveTopic(id, newParentPath = null) {
    return this.put(`/reference/topics/${id}/move`, null, {
      params: { new_parent_path: newParentPath }
    });
  }

  // Geolocations
  async getGeolocations(params = {}) {
    return this.get('/reference/geolocations', { params });
  }

  async getGeolocationsTree() {
    return this.get('/reference/geolocations/tree');
  }

  async getCountries() {
    return this.get('/reference/geolocations/countries');
  }

  async getRegions(countryId) {
    return this.get(`/reference/geolocations/${countryId}/regions`);
  }

  async getCities(regionId) {
    return this.get(`/reference/geolocations/${regionId}/cities`);
  }

  async getGeolocation(id) {
    return this.get(`/reference/geolocations/${id}`);
  }

  async createGeolocation(data) {
    return this.post('/reference/geolocations', data);
  }

  async updateGeolocation(id, data) {
    return this.put(`/reference/geolocations/${id}`, data);
  }

  async deleteGeolocation(id) {
    return this.delete(`/reference/geolocations/${id}`);
  }

  // Authors
  async getAuthors(params = {}) {
    return this.get('/reference/authors', { params });
  }

  async getVerifiedAuthors() {
    return this.get('/reference/authors/verified');
  }

  async getTopAuthors(limit = 100) {
    return this.get('/reference/authors/top', { params: { limit } });
  }

  async getAuthor(id) {
    return this.get(`/reference/authors/${id}`);
  }

  async createAuthor(data) {
    return this.post('/reference/authors', data);
  }

  async updateAuthor(id, data) {
    return this.put(`/reference/authors/${id}`, data);
  }

  async deleteAuthor(id) {
    return this.delete(`/reference/authors/${id}`);
  }

  async verifyAuthor(id) {
    return this.put(`/reference/authors/${id}/verify`);
  }

  // Bulk operations
  async bulkImport(entityType, data, updateExisting = false) {
    return this.post('/reference/bulk-import', {
      entity_type: entityType,
      data,
      update_existing: updateExisting
    });
  }

  async exportData(entityType, format = 'json', filters = null) {
    const params = { format };
    if (filters) {
      params.filters = JSON.stringify(filters);
    }
    
    const response = await this.get(`/reference/export/${entityType}`, {
      params,
      responseType: 'blob'
    });
    
    return response;
  }
}

export const referenceApi = new ReferenceApiClient();